import type { EntityRef, ProductListItemDto } from '@repo/api/web/models'
import { injectable, inject } from 'inversify'

import { RoutingService, type RoutingServiceRouteData } from '#AltServices'
import { Translit } from '#Common/support'
import type { CategoryDto } from '#Products/models/Pim/CategoryDto'
import {
  resolveCategoryDestination,
  type CategoryDestination,
  type CategoryDestinationInput,
} from '#Products/support/categoryDestination'

/** Категория, из которой можно построить слаг: backend-слаг либо name+id */
type SluggableCategory = Pick<EntityRef, 'id'>
  & Partial<Pick<EntityRef, 'displayName'>>
  & Partial<Pick<CategoryDto, 'name' | 'slug'>>

@injectable()
export class ProductsRoutingService {
  constructor(
    @inject(RoutingService) readonly router: RoutingService,
    @inject(Translit) readonly translit: Translit,
  ) {}

  get index() {
    return this.router.createRouteData({ name: 'ProductsIndex' })
  }

  /** Storefront home (`/`) — discovery shell (not a product listing) */
  getIndex() {
    return this.router.createRouteData({ name: 'ProductsIndex' })
  }

  getDetails(
    product: ProductListItemDto,
    category?: SluggableCategory | string,
  ) {
    // category как строка — это уже готовый слаг (напр. из query при 301)
    const categorySlug
      = typeof category === 'string'
        ? category
        : category != null
          ? this.getCategorySlug(category)
          : undefined

    return this.router.createRouteData({
      name: 'ProductsDetails',
      params: { id: this.getProductSlug(product) },
      query: categorySlug ? { category: categorySlug } : undefined,
    })
  }

  /**
   * Слаг товара: приоритет backend-слага (`ProductChannel.Slug`), fallback —
   * клиентский `translit(Name)-id` (тот же формат) на время ленивого бэкфилла
   */
  getProductSlug(product: ProductListItemDto) {
    if (product.slug) return product.slug
    return this.buildSlug(product.wellKnownAttributes?.Name, product.id)
  }

  /**
   * Слаг категории: приоритет backend-слага, fallback — `translit(Name)-id`
   * (детерминирован, коллизий нет — id уникален; совпадает с backend-форматом)
   */
  getCategorySlug(category: SluggableCategory) {
    if (category.slug) return category.slug
    return this.buildSlug(category.name ?? category.displayName, category.id)
  }

  extractIdFromSlug(slug: string) {
    return parseInt(slug.split('-').at(-1)!)
  }

  /** ЧПУ категории: `/catalog/:categorySlug` (без категории — корень каталога) */
  getCatalog(category?: SluggableCategory) {
    return this.router.createRouteData({
      name: 'ProductsCatalog',
      params: { category: category ? this.getCategorySlug(category) : undefined },
    })
  }

  /** ЧПУ категории по готовому слагу (напр. из query товара) */
  getCatalogBySlug(categorySlug: string) {
    return this.router.createRouteData({
      name: 'ProductsCatalog',
      params: { category: categorySlug },
    })
  }

  /**
   * R1–R3 destination type for a category (delegates to pure util).
   * Prefer this or `resolveCategoryDestination` over ad-hoc leaf checks.
   */
  resolveDestination(category: CategoryDestinationInput): CategoryDestination {
    return resolveCategoryDestination(category)
  }

  /**
   * Where a category navigates: page type + `/catalog/:slug` route.
   * Hub and PLP share the same URL pattern in v1; consumers use `type`
   * for page-type / chrome decisions (mega-menu, hubs, breadcrumbs).
   */
  getCatalogDestination(category: SluggableCategory & CategoryDestinationInput): {
    type: CategoryDestination
    route: RoutingServiceRouteData
  } {
    return {
      type: this.resolveDestination(category),
      route: this.getCatalog(category),
    }
  }

  /**
   * Free-text search page (`/search?q=`).
   * `/catalog?q=` remains a working legacy query PLP but is not the navigation target.
   */
  getSearch(query?: string) {
    return this.router.createRouteData({
      name: 'ProductsSearch',
      query: query ? { q: query } : undefined,
    })
  }

  protected buildSlug(name: string | undefined, id: number) {
    const translit = this.translit.transform(name ?? '')
    return `${translit?.toLowerCase()}-${id}`
  }
}
